import { Extension, type Editor } from '@tiptap/react';
import { Plugin, PluginKey, NodeSelection } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';

/**
 * BlockHandle — ховер-гаттер «+ / ⠿» слева у каждого блока (как в Teamly/Notion).
 *
 * Поведение:
 *  - при наведении на блок слева всплывают две кнопки: «+» (добавить блок —
 *    вставляет пустой абзац ниже и открывает «/»-меню) и «⠿» (перетащить блок).
 *  - «⠿» — нативный HTML5-drag: выделяем узел как NodeSelection и отдаём
 *    ProseMirror'у (view.dragging), он сам переносит блок при отпускании.
 *  - по умолчанию гаттер скрыт; появляется только при mousemove над блоком.
 *
 * Без новых зависимостей (чистый ProseMirror-плагин + DOM). Всё в try/catch —
 * любая ошибка позиционирования/драга не должна ронять редактор.
 */
export const BlockHandle = Extension.create({
  name: 'blockHandle',
  addProseMirrorPlugins() {
    return [blockHandlePlugin(this.editor)];
  },
});

function blockHandlePlugin(editor: Editor): Plugin {
  return new Plugin({
    key: new PluginKey('blockHandle'),
    view(view) {
      return new BlockHandleView(view, editor);
    },
  });
}

class BlockHandleView {
  private gutter: HTMLDivElement;
  private addBtn: HTMLButtonElement;
  private dragBtn: HTMLButtonElement;
  private wrapper: HTMLElement;
  private currentEl: HTMLElement | null = null;

  constructor(
    private readonly view: EditorView,
    private readonly editor: Editor,
  ) {
    // Контейнер для позиционирования — обёртка редактора (position: relative).
    this.wrapper =
      (view.dom.closest('.tiptap-wrapper') as HTMLElement | null) ??
      (view.dom.parentElement as HTMLElement);

    this.gutter = document.createElement('div');
    this.gutter.className = 'pm-block-gutter';
    this.gutter.contentEditable = 'false';

    this.addBtn = document.createElement('button');
    this.addBtn.type = 'button';
    this.addBtn.className = 'pm-block-gutter-btn';
    this.addBtn.title = 'Добавить блок';
    this.addBtn.textContent = '+';

    this.dragBtn = document.createElement('button');
    this.dragBtn.type = 'button';
    this.dragBtn.className = 'pm-block-gutter-btn pm-block-gutter-drag';
    this.dragBtn.title = 'Перетащить блок';
    this.dragBtn.setAttribute('draggable', 'true');
    this.dragBtn.textContent = '⠿';

    this.gutter.append(this.addBtn, this.dragBtn);
    this.wrapper.appendChild(this.gutter);

    view.dom.addEventListener('mousemove', this.onMouseMove);
    this.wrapper.addEventListener('mouseleave', this.onWrapperLeave);
    this.addBtn.addEventListener('click', this.onAdd);
    this.dragBtn.addEventListener('dragstart', this.onDragStart);
    this.dragBtn.addEventListener('dragend', this.onDragEnd);
    // Не даём редактору терять фокус/выделять при клике по самому гаттеру.
    this.gutter.addEventListener('mousedown', (e) => {
      if (e.target !== this.dragBtn) e.preventDefault();
    });
  }

  /** Находит топ-уровневый блок-элемент под курсором по вертикали. */
  private blockAt(clientY: number): HTMLElement | null {
    const blocks = Array.from(this.view.dom.children) as HTMLElement[];
    for (const b of blocks) {
      const r = b.getBoundingClientRect();
      if (clientY >= r.top - 4 && clientY <= r.bottom + 4) return b;
    }
    return null;
  }

  private onMouseMove = (e: MouseEvent): void => {
    try {
      const el = this.blockAt(e.clientY);
      if (!el) {
        this.hide();
        return;
      }
      this.currentEl = el;
      const wrapRect = this.wrapper.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      // Размещаем в левом «жёлобе» (контент имеет left-padding 56px) — гаттер
      // шириной ~42px от left:8px заканчивается ~50px, не налезая на текст.
      this.gutter.style.top = `${r.top - wrapRect.top + 2}px`;
      this.gutter.style.left = '8px';
      this.gutter.classList.add('is-visible');
    } catch {
      this.hide();
    }
  };

  private onWrapperLeave = (): void => this.hide();

  private hide(): void {
    this.gutter.classList.remove('is-visible');
  }

  /** «+»: вставить пустой абзац после блока и открыть «/»-меню. */
  private onAdd = (e: MouseEvent): void => {
    e.preventDefault();
    if (!this.currentEl) return;
    try {
      const start = this.view.posAtDOM(this.currentEl, 0);
      const $pos = this.view.state.doc.resolve(start);
      const after = $pos.after(1);
      this.editor
        .chain()
        .focus()
        .insertContentAt(after, { type: 'paragraph' })
        .setTextSelection(after + 1)
        .insertContent('/')
        .run();
    } catch {
      /* no-op */
    }
  };

  /** «⠿»: стартуем нативный drag, отдаём узел ProseMirror'у. */
  private onDragStart = (e: DragEvent): void => {
    if (!this.currentEl || !e.dataTransfer) return;
    try {
      const start = this.view.posAtDOM(this.currentEl, 0);
      const $pos = this.view.state.doc.resolve(start);
      const nodePos = $pos.before(1);
      const sel = NodeSelection.create(this.view.state.doc, nodePos);
      this.view.dispatch(this.view.state.tr.setSelection(sel));
      const slice = sel.content();
      // ProseMirror сам выполнит перенос при drop.
      (this.view as unknown as { dragging: unknown }).dragging = { slice, move: true };
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/html', this.currentEl.outerHTML);
      this.currentEl.classList.add('pm-block-dragging');
    } catch {
      /* no-op */
    }
  };

  private onDragEnd = (): void => {
    this.view.dom
      .querySelectorAll('.pm-block-dragging')
      .forEach((el) => el.classList.remove('pm-block-dragging'));
  };

  destroy(): void {
    this.view.dom.removeEventListener('mousemove', this.onMouseMove);
    this.wrapper.removeEventListener('mouseleave', this.onWrapperLeave);
    this.gutter.remove();
  }
}
